import apiClient from '@/lib/api/apiClient'
import { throwServiceError } from './serviceHelpers'

/** Coerce GET `/api/notes/:id` payloads to an array (top-level array or common wrappers). */
function normalizeNotesListPayload(payload: unknown): unknown[] {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  if (typeof payload === 'object') {
    const o = payload as Record<string, unknown>
    if (Array.isArray(o.data)) return o.data
    if (Array.isArray(o.notes)) return o.notes
    if (Array.isArray(o.rows)) return o.rows
  }
  return []
}

/** POST `/api/notes/:targetId` — matches Angular `NoteAddComponent`. */
export async function postApplicationScopedNote(
  targetId: string | number,
  body: { notes: string; notes_for: string; source?: string },
): Promise<void> {
  try {
    await apiClient.post(`/api/notes/${targetId}`, {
      notes: body.notes,
      notes_for: body.notes_for,
      ...(body.source ? { source: body.source } : {}),
    })
  } catch (error) {
    throwServiceError(error, 'Failed to add note.')
  }
}

/** Customer-service note on application ID (`fromPage="APPLICATION"`). */
export async function addApplicationNote(
  applicationId: string | number,
  notes: string,
): Promise<void> {
  return postApplicationScopedNote(applicationId, { notes, notes_for: 'APPLICATION' })
}

/** GET /api/notes/:id?notes_for=APPLICATION — Angular NoteListComponent.loadNotes(). */
export async function getApplicationNotes(
  applicationId: string | number,
): Promise<unknown[]> {
  try {
    const response = await apiClient.get(`/api/notes/${applicationId}`, {
      params: { notes_for: 'APPLICATION' },
    })
    return normalizeNotesListPayload(response.data)
  } catch (error) {
    throwServiceError(error, 'Failed to load notes.')
  }
}

/** GET /api/notes/:applicantId?notes_for=UNDERWRITER&source=INTERNAL — Angular credit-tab note list. */
export async function getUnderwriterNotes(
  applicantId: string | number,
): Promise<unknown[]> {
  try {
    const response = await apiClient.get(`/api/notes/${applicantId}`, {
      params: { notes_for: 'UNDERWRITER', source: 'INTERNAL' },
    })
    return normalizeNotesListPayload(response.data)
  } catch (error) {
    throwServiceError(error, 'Failed to load underwriting notes.')
  }
}

/** Underwriting checklist note on applicant ID (`fromPage="UNDERWRITER"`, `source="INTERNAL"`). */
export async function addUnderwriterApplicantNote(
  applicantId: string | number,
  notes: string,
): Promise<void> {
  return postApplicationScopedNote(applicantId, {
    notes,
    notes_for: 'UNDERWRITER',
    source: 'INTERNAL',
  })
}
